/**
 * ProjectStorageService — CRUD over the `projects` table.
 *
 * Kept separate from SupabaseFlowStorageService (already 1300+ lines) so
 * project concerns stay isolated. Mirrors the same pattern: row mapping
 * helpers, an explicit row interface, and a singleton instance export.
 *
 * Inbox is NOT stored here — flows with `projectId === null` are the
 * Inbox group, synthesized by the UI. See `src/types/project.ts`.
 */

import { supabase } from '../lib/supabase';
import { getCurrentUserId } from '../lib/currentUser';
import { settleFlowSaves } from './pendingFlowSaves';
import logger from '../utils/logger';
import type {
  Project,
  ProjectCreateInput,
  ProjectUpdateInput,
  ProjectFilter,
} from '../types/project';
import { INBOX_PROJECT_ID } from '../types/project';

interface ProjectRow {
  id: string;
  user_id: string;
  org_id: string | null;
  name: string;
  description: string | null;
  color: string | null;
  emoji: string | null;
  sort_order: number;
  archived: boolean;
  created_at: string;
  updated_at: string;
}

function rowToProject(row: ProjectRow): Project {
  return {
    id: row.id,
    userId: row.user_id,
    orgId: row.org_id,
    name: row.name,
    description: row.description,
    color: row.color,
    emoji: row.emoji,
    sortOrder: row.sort_order ?? 0,
    archived: row.archived ?? false,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

class ProjectStorageService {
  // Reads the stored session instead of `auth.getUser()`, which is a network
  // GET behind the auth mutex — with the dashboard loading flows and projects
  // together, two of those serialized ahead of either query.
  private async getUserId(): Promise<string | null> {
    return getCurrentUserId();
  }

  /**
   * List the authenticated user's projects, ordered by sortOrder ASC
   * then name ASC. Archived projects are included — callers filter as
   * needed (the dashboard typically excludes them; the archive view
   * shows only them).
   */
  async listProjects(): Promise<Project[]> {
    const userId = await this.getUserId();
    if (!userId) {
      logger.warn('[ProjectStorage] Not authenticated, returning empty project list');
      return [];
    }

    const { data, error } = await supabase
      .from('projects')
      .select('*')
      .eq('user_id', userId)
      .order('sort_order', { ascending: true })
      .order('name', { ascending: true });

    if (error) {
      logger.error('[ProjectStorage] listProjects failed:', error);
      throw new Error(`Failed to list projects: ${error.message}`);
    }

    return (data || []).map((row) => rowToProject(row as ProjectRow));
  }

  async getProject(id: string): Promise<Project | null> {
    const { data, error } = await supabase
      .from('projects')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      // PostgREST returns PGRST116 when single() finds nothing — that's
      // a 404, not a real error.
      if ((error as any).code === 'PGRST116') return null;
      logger.error('[ProjectStorage] getProject failed:', error);
      throw new Error(`Failed to get project: ${error.message}`);
    }
    return data ? rowToProject(data as ProjectRow) : null;
  }

  async createProject(input: ProjectCreateInput): Promise<Project> {
    const userId = await this.getUserId();
    if (!userId) {
      throw new Error('Not authenticated');
    }

    const trimmedName = input.name.trim();
    if (!trimmedName) {
      throw new Error('Project name is required');
    }

    const { data, error } = await supabase
      .from('projects')
      .insert({
        user_id: userId,
        org_id: input.orgId ?? null,
        name: trimmedName,
        description: input.description ?? null,
        color: input.color ?? null,
        emoji: input.emoji ?? null,
      })
      .select()
      .single();

    if (error) {
      logger.error('[ProjectStorage] createProject failed:', error);
      throw new Error(`Failed to create project: ${error.message}`);
    }

    return rowToProject(data as ProjectRow);
  }

  async updateProject(id: string, patch: ProjectUpdateInput): Promise<Project> {
    // Build the row patch — only include keys the caller provided so we
    // don't overwrite columns with `undefined`.
    const rowPatch: Record<string, unknown> = {};
    if (patch.name !== undefined) rowPatch.name = patch.name.trim();
    if (patch.description !== undefined) rowPatch.description = patch.description;
    if (patch.color !== undefined) rowPatch.color = patch.color;
    if (patch.emoji !== undefined) rowPatch.emoji = patch.emoji;
    if (patch.sortOrder !== undefined) rowPatch.sort_order = patch.sortOrder;
    if (patch.archived !== undefined) rowPatch.archived = patch.archived;

    const { data, error } = await supabase
      .from('projects')
      .update(rowPatch)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      logger.error('[ProjectStorage] updateProject failed:', error);
      throw new Error(`Failed to update project: ${error.message}`);
    }
    return rowToProject(data as ProjectRow);
  }

  /**
   * Delete a project. Flows in the project fall back to Inbox
   * (`project_id IS NULL`) because of the ON DELETE SET NULL FK from
   * migration 095_projects.sql — no data is lost.
   */
  async deleteProject(id: string): Promise<void> {
    const { error } = await supabase
      .from('projects')
      .delete()
      .eq('id', id);

    if (error) {
      logger.error('[ProjectStorage] deleteProject failed:', error);
      throw new Error(`Failed to delete project: ${error.message}`);
    }
  }

  /**
   * Move a flow into a project (or Inbox).
   *
   * @param flowId
   * @param target - a real project id, INBOX_PROJECT_ID, or null. Null
   *   and INBOX_PROJECT_ID are treated the same way — they set
   *   `project_id` to NULL.
   */
  async moveFlowToProject(flowId: string, target: ProjectFilter): Promise<void> {
    // A save backgrounded by leaving the editor upserts the whole row,
    // project_id included, so it would undo this move if it landed after it.
    await settleFlowSaves(flowId);

    const projectId =
      target === INBOX_PROJECT_ID || target == null ? null : target;

    const { error } = await supabase
      .from('flows')
      .update({ project_id: projectId })
      .eq('id', flowId);

    if (error) {
      logger.error('[ProjectStorage] moveFlowToProject failed:', error);
      throw new Error(`Failed to move flow: ${error.message}`);
    }
  }

  /**
   * Bulk-move a batch of flows. Returns the count moved. Implemented as
   * a single UPDATE rather than per-row so the UI can show one toast on
   * completion.
   */
  async bulkMoveFlowsToProject(
    flowIds: string[],
    target: ProjectFilter
  ): Promise<number> {
    if (flowIds.length === 0) return 0;
    // Same ordering concern as moveFlowToProject, across the whole batch.
    await Promise.all(flowIds.map((id) => settleFlowSaves(id)));

    const projectId =
      target === INBOX_PROJECT_ID || target == null ? null : target;

    const { data, error } = await supabase
      .from('flows')
      .update({ project_id: projectId })
      .in('id', flowIds)
      .select('id');

    if (error) {
      logger.error('[ProjectStorage] bulkMoveFlowsToProject failed:', error);
      throw new Error(`Failed to move flows: ${error.message}`);
    }
    return (data || []).length;
  }
}

export const projectStorageService = new ProjectStorageService();
export default projectStorageService;
